'use client'

import React from 'react'

import { useBranding } from '@/contexts/BrandingContext'

interface LogoProps {
  rgb?: number[]
  width?: string
  /** Accessible name. Defaults to the brand name; pass "" where decorative. */
  alt?: string
}

/**
 * The brand mark: the network's own on a dedicated space, Communo's everywhere else.
 *
 * The accessible name follows the image. A territory's logo announced as "Communo" is the
 * same leak as the wrong picture, except only screen-reader users hear it — which makes it
 * the version that survives review. An explicit `alt` still wins, including `""` where the
 * mark sits next to the name it would otherwise repeat.
 */
export const Logo = ({ width, alt }: LogoProps) => {
  const branding = useBranding()
  const src = branding?.logoUrl ?? '/illustrations/svg/logo.svg'
  const name = alt ?? branding?.networkName ?? 'Communo'

  return (
    <img
      src={src}
      height={'auto'}
      width={width}
      alt={name}
      // A network logo is an arbitrary upload with an arbitrary aspect ratio, while the
      // packaged mark is drawn to the width it is given. Capping the height keeps a tall logo
      // from pushing the header open; `object-fit` keeps it from being squashed to fit.
      style={
        branding?.logoUrl
          ? { maxHeight: '40px', objectFit: 'contain' }
          : undefined
      }
    />
  )
}
